import type { DocBlock, DocumentModel } from "@/lib/tool-kit/document";
import {
  countWords,
  lowerFirstWord,
} from "@/lib/tool-kit/essay-templates/text";
import { ensureSentence } from "@/lib/tool-kit/research-writing/limitations";
import { clause } from "@/lib/tool-kit/research-writing/research-gap";
import { paragraphFromParts } from "@/lib/tool-kit/research-writing/specific-aims";

/**
 * A problem statement in the ideal–reality–consequences pattern, with the proposal as a
 * final sentence.
 *
 * The pattern is a staple of technical-writing and proposal teaching: set the ideal
 * beside the reality so the gap between them shows, then say what follows if it is left
 * alone. Booth, Colomb and Williams's *The Craft of Research* names the test every such
 * statement must pass — the reader's "So what?". The writer's sentences are placed as
 * written; the tool adds the connector between ideal and reality, and the proposal's
 * lead-in. A problem statement describes a problem and does not prescribe a fix, so the
 * proposal stays a separate, last sentence.
 */

export type Connector = "however" | "but" | "yet" | "reality" | "none";
export const CONNECTORS: readonly Connector[] = [
  "however",
  "but",
  "yet",
  "reality",
  "none",
];

export type StatementFormat = "paragraph" | "parts";

export interface ProblemInput {
  title: string;
  /** A sentence: what should be happening. */
  ideal: string;
  connector: Connector;
  /** A sentence: what is happening, and to whom. */
  reality: string;
  /** A sentence: the evidence for the reality. */
  evidence: string;
  /** A sentence: what follows if nothing changes. */
  consequences: string;
  /** A verb phrase: what this study will do. */
  proposal: string;
  format: StatementFormat;
}

function has(text: string): boolean {
  return text.trim() !== "";
}

export interface ProblemLabels {
  defaultTitle: string;
  subtitle: string;
  heading: string;
  headings: {
    ideal: string;
    reality: string;
    consequences: string;
    proposal: string;
  };
  tip: string;
  connectors: Record<Exclude<Connector, "none">, string>;
  proposalFrame: string;
  prompts: {
    ideal: string;
    reality: string;
    consequences: string;
    proposal: string;
  };
}

/** The reality sentence, led by the chosen connector. */
export function realitySentence(
  input: ProblemInput,
  labels: ProblemLabels,
  locale: string
): string {
  const reality = ensureSentence(input.reality);
  if (reality === "") {
    return "";
  }
  if (input.connector === "none") {
    return reality;
  }
  return `${labels.connectors[input.connector]} ${lowerFirstWord(reality, locale)}`;
}

export function proposalSentence(
  input: ProblemInput,
  labels: ProblemLabels,
  locale: string
): string {
  const approach = clause(input.proposal);
  if (approach === "") {
    return "";
  }
  return labels.proposalFrame.replace(
    "{proposal}",
    lowerFirstWord(approach, locale)
  );
}

/** The five parts as sentences, in order: ideal, reality, evidence, consequences, proposal. */
export function statementParts(
  input: ProblemInput,
  labels: ProblemLabels,
  locale: string
): string[] {
  return [
    ensureSentence(input.ideal),
    realitySentence(input, labels, locale),
    ensureSentence(input.evidence),
    ensureSentence(input.consequences),
    proposalSentence(input, labels, locale),
  ];
}

export function statementText(
  input: ProblemInput,
  labels: ProblemLabels,
  locale: string
): string {
  return statementParts(input, labels, locale)
    .filter((part) => part !== "")
    .join(" ");
}

export function statementWords(
  input: ProblemInput,
  labels: ProblemLabels,
  locale: string
): number {
  return countWords(statementText(input, labels, locale));
}

const SOLUTION_PATTERN =
  /\b(?:the solution is|we (?:should|must|need to) (?:implement|introduce|build|adopt|install)|should be (?:implemented|introduced|adopted))\b/i;
const NUMBER_OR_CITATION = /\d|\([^)]*\b(?:19|20)\d{2}\b[^)]*\)|\[[^\]]+\]/;

export type ProblemCheckId =
  | "ideal"
  | "reality"
  | "evidence"
  | "consequences"
  | "proposal"
  | "solution";

export interface ProblemCheck {
  id: ProblemCheckId;
  state: "done" | "todo" | "note";
}

export function problemChecks(input: ProblemInput): ProblemCheck[] {
  const simple = (id: "ideal" | "reality" | "consequences" | "proposal") =>
    ({ id, state: has(input[id]) ? "done" : "todo" }) as ProblemCheck;
  const backed =
    has(input.evidence) ||
    NUMBER_OR_CITATION.test(`${input.reality}\n${input.consequences}`);
  const leaking = SOLUTION_PATTERN.test(
    `${input.ideal}\n${input.reality}\n${input.consequences}`
  );
  return [
    simple("ideal"),
    simple("reality"),
    {
      id: "evidence",
      state: has(input.reality) && !backed ? "note" : "done",
    },
    simple("consequences"),
    simple("proposal"),
    { id: "solution", state: leaking ? "note" : "done" },
  ];
}

export function buildProblemDocument(
  input: ProblemInput,
  labels: ProblemLabels,
  locale: string
): DocumentModel {
  const parts = statementParts(input, labels, locale);
  const [ideal, reality, evidence, consequences, proposal] = parts;
  let body: DocBlock[];
  if (input.format === "paragraph") {
    const run = (text: string, prompt: string) => ({ text, prompt });
    body = paragraphFromParts([
      run(ideal, labels.prompts.ideal),
      run(
        [reality, evidence].filter(Boolean).join(" "),
        labels.prompts.reality
      ),
      run(consequences, labels.prompts.consequences),
      run(proposal, labels.prompts.proposal),
    ]);
  } else {
    const section = (
      label: string,
      text: string,
      prompt: string
    ): DocBlock[] => [
      { kind: "subheading", text: label },
      ...(text
        ? [{ kind: "paragraph", text } as DocBlock]
        : [{ kind: "prompt", text: prompt } as DocBlock]),
    ];
    body = [
      ...section(labels.headings.ideal, ideal, labels.prompts.ideal),
      ...section(
        labels.headings.reality,
        [reality, evidence].filter(Boolean).join(" "),
        labels.prompts.reality
      ),
      ...section(
        labels.headings.consequences,
        consequences,
        labels.prompts.consequences
      ),
      ...section(labels.headings.proposal, proposal, labels.prompts.proposal),
    ];
  }
  return {
    title: input.title.trim() || labels.defaultTitle,
    subtitle: labels.subtitle,
    sections: [
      {
        heading: labels.heading,
        blocks: [{ kind: "guidance", text: labels.tip }, ...body],
      },
    ],
  };
}
